import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { Plus, X } from 'lucide-react';
import {
  type AlertOverride,
  useAlertOverrides,
  useCreateAlertOverride,
  useDeleteAlertOverride,
} from '@/hooks/useAlertOverrides';
import { useAuth } from '@/hooks/useAuth';
import { useDevices } from '@/hooks/useDevices';
import { useRegions } from '@/hooks/useRegions';
import { useDivisions } from '@/hooks/useDivisions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * OverridesPanel — manage AlertOverride rows for a task.
 * Inline add-override form expanded via header button. Table of
 * existing overrides with scope/dedupe/expiry + Remove button.
 *
 * Rendered inside AlertDetails main column.
 */
export function OverridesPanel({ taskId }: { taskId: string }) {
  const { data: overrides, isLoading } = useAlertOverrides(taskId);
  const createOverride = useCreateAlertOverride();
  const deleteOverride = useDeleteAlertOverride();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newType, setNewType] = useState('silence');
  const [newReason, setNewReason] = useState('');
  const [newHours, setNewHours] = useState(24);

  // Resolve a scoped override to the name of its target (device / region /
  // division). Use the active context's org so it's correct for MSP context
  // switching. These queries are shared/cached, so this is a cache hit in
  // practice.
  const { user } = useAuth();
  const orgId = user?.activeContext?.organizationId ?? user?.organizationId ?? undefined;
  const { data: devices } = useDevices(orgId);
  const { data: regions } = useRegions(orgId);
  const { data: divisions } = useDivisions(orgId);
  const deviceNameById = useMemo(
    () => new Map((devices ?? []).map((d) => [d.id, d.name])),
    [devices],
  );
  const regionNameById = useMemo(
    () => new Map((regions ?? []).map((r) => [r.id, r.name])),
    [regions],
  );
  const divisionNameById = useMemo(
    () => new Map((divisions ?? []).map((d) => [d.id, d.name])),
    [divisions],
  );

  const handleAdd = () => {
    let silenceUntil: string | undefined;
    if (newType === 'silence' && newHours > 0) {
      const until = new Date();
      until.setHours(until.getHours() + newHours);
      silenceUntil = until.toISOString();
    }
    createOverride.mutate(
      {
        taskId,
        data: {
          overrideType: newType,
          silenceUntil,
          silenceReason: newReason || undefined,
        },
      },
      {
        onSuccess: () => {
          setShowAddForm(false);
          setNewReason('');
        },
      },
    );
  };

  const columns: readonly ResponsiveTableColumn<AlertOverride>[] = [
    {
      key: 'type',
      label: 'Type',
      render: (o) => (
        <Badge variant="secondary" className="font-normal capitalize">
          {o.overrideType}
        </Badge>
      ),
    },
    {
      key: 'scope',
      label: 'Scope',
      render: (o) =>
        o.deviceId
          ? 'Device'
          : o.regionId
            ? 'Region'
            : o.divisionId
              ? 'Division'
              : 'All',
    },
    {
      key: 'instance',
      label: 'Instance',
      hideAt: 'mobile',
      // Mirror the scope: name the specific target it applies to. Regions and
      // devices have detail pages, so they link; divisions don't, so plain text.
      render: (o) => {
        if (o.deviceId) {
          return (
            <Link
              to={`/devices/${o.deviceId}`}
              className="font-medium text-primary hover:underline"
            >
              {deviceNameById.get(o.deviceId) ?? 'Device'}
            </Link>
          );
        }
        if (o.regionId) {
          return (
            <Link
              to={`/regions/${o.regionId}`}
              className="font-medium text-primary hover:underline"
            >
              {regionNameById.get(o.regionId) ?? 'Region'}
            </Link>
          );
        }
        if (o.divisionId) {
          return <span>{divisionNameById.get(o.divisionId) ?? 'Division'}</span>;
        }
        return <span className="text-muted-foreground">All</span>;
      },
    },
    {
      key: 'until',
      label: 'Until',
      render: (o) => (
        <span className="text-xs">
          {o.silenceUntil
            ? formatDistanceToNow(new Date(o.silenceUntil), { addSuffix: true })
            : 'Indefinite'}
        </span>
      ),
    },
    {
      key: 'reason',
      label: 'Reason',
      hideAt: 'mobile',
      render: (o) => o.silenceReason ?? <span className="text-muted-foreground">—</span>,
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Overrides</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setShowAddForm(!showAddForm); }}
        >
          {showAddForm ? (
            <>
              <X className="w-3.5 h-3.5" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              Add override
            </>
          )}
        </Button>
      </div>

      {showAddForm ? (
        <div className="rounded-md border border-border bg-bg-subtle p-4 mb-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="override-type" className="text-xs">Type</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger id="override-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="silence">Silence</SelectItem>
                  <SelectItem value="disable">Disable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newType === 'silence' ? (
              <div className="space-y-1.5">
                <Label htmlFor="override-duration" className="text-xs">Duration</Label>
                <Select
                  value={String(newHours)}
                  onValueChange={(v) => { setNewHours(parseInt(v, 10)); }}
                >
                  <SelectTrigger id="override-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="4">4 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                    <SelectItem value="0">Indefinite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="override-reason" className="text-xs">Reason</Label>
            <Input
              id="override-reason"
              value={newReason}
              onChange={(e) => { setNewReason(e.target.value); }}
              placeholder="Optional"
            />
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={handleAdd} disabled={createOverride.isPending}>
              {createOverride.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : overrides && overrides.length > 0 ? (
        <ResponsiveTable<AlertOverride>
          columns={columns}
          rows={overrides}
          rowKey={(r) => r.id}
          rowActions={(o) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                deleteOverride.mutate({ taskId, overrideId: o.id });
              }}
              disabled={deleteOverride.isPending}
              className="text-destructive hover:text-destructive"
            >
              Remove
            </Button>
          )}
        />
      ) : !showAddForm ? (
        <p className="text-sm text-muted-foreground">
          No overrides configured for this task.
        </p>
      ) : null}
    </>
  );
}
